import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import i18n from 'i18next'
import * as R from 'ramda'
import Anchor from '../../UniformComponents/Anchor'
import Tooltip from '../../UniformComponents/Tooltip'
import Select from '../form/Select'
import ClientEdit from '../Client/ClientEdit'
import ClientStore from '../Client/ClientStore'
import { unescapeHtmlTags } from '../helpers'

import './sidebar.scss'

@observer
class Sidebar extends Component {
    constructor(props) {
        super(props)
        this.contentRef = React.createRef()
        const { store } = this.props
        this.state = {
            curTabInd: 0,
            menuOptions: { mainMenu: [], userMenu: [] },
            overflowActive: false,
            userClientId: R.pathOr(null, ['userData', 'clientId'], store),
            isExpand: false,
            selectedUserId: undefined,
            showAvatarMenu: false,
        }
        this.maxbiStatus = store.getMaxbiStatus()
        this.clientStore = new ClientStore(store)
        this.disposeReaction = null
        this.formRef = React.createRef()
        this.avatarMenuRef = React.createRef()
        this.avatarButtonRef = React.createRef()
    }

    componentDidMount() {
        const mainMenu = this.filterMenuOptions(this.mainMenuOptions())
        const userMenu = this.filterMenuOptions(this.userMenuOptions())

        this.setState({ menuOptions: { mainMenu, userMenu } })

        // set the curTabInd from page reload
        this.updateCurTabIndFromURL(mainMenu, userMenu)
        // set the curTabInd by listening to back & forward browser navigation
        window.onpopstate = () => {
            this.updateCurTabIndFromURL(mainMenu, userMenu)
        }
        // check sidebar overflow to add style
        window.addEventListener('resize', this.checkOverflow)
        document.addEventListener('mousedown', this.toggleAvatarMenu)

        // highlight templates after survey is loaded
        const { store } = this.props
        this.disposeReaction = reaction(
            () => store.isTemplate,
            () => this.updateCurTabIndFromURL(mainMenu, userMenu),
        )
        this.isSwitchAccountsEnabled = process.env.USE_NEW_HEADER_UI && this.canUserSwitchAccounts(store)
        if (this.isSwitchAccountsEnabled) store.setShowUserDropdown(true)
    }

    componentDidUpdate(prevProps, prevState) {
        const { menuOptions: { mainMenu } } = this.state
        if (prevState.menuOptions.mainMenu.length !== mainMenu.length) {
            this.checkOverflow()
        }
    }

    componentWillUnmount() {
        window.onpopstate = null
        window.removeEventListener('resize', this.checkOverflow)
        document.removeEventListener('mousedown', this.toggleAvatarMenu)
        if (this.disposeReaction) this.disposeReaction()
    }

    canUserSwitchAccounts = (store) => {
        const userRole = R.path(['userData', 'role'], store)
        return ((store.isExploriAdmin() && [100, 110].includes(userRole))
                || R.pathOr(false, ['userData', 'isReseller'], store))
                && !store.isLoginChoose
                && store.userAccounts?.length > 0
    }

    // find height diff of main-menu on overflow
    checkOverflow = () => {
        const element = this.contentRef.current
        if (element.scrollHeight > element.clientHeight) {
            this.setState({ overflowActive: true })
        } else {
            this.setState({ overflowActive: false })
        }
    }

    // find curTabInd from pathname
    updateCurTabIndFromURL = (mainMenu, userMenu) => {
        const path = window.location.pathname
        const segments = path.split('/')
        const paths = segments[1].length !== 0 && segments.join('/')
        const { store } = this.props

        if (paths) {
            let menuInd = mainMenu.findIndex((menu) => (menu.url !== '/' && paths.startsWith(menu.url)))
            if (menuInd === -1) {
                menuInd = mainMenu.findIndex((menu) => (
                    menu.highlightOnUrls && menu.highlightOnUrls.some((url) => paths.startsWith(url))
                ))
            }
            if (menuInd !== -1) {
                const searchParams = new URLSearchParams(window.location.search)
                const isTemplateWizard = searchParams.get('surveytype') === 'T'
                const highlightTemplateTab = menuInd === 2 && (isTemplateWizard || store.isTemplate)
                const templateIdx = mainMenu.findIndex((opt) => opt.url === '/survey-templates')
                this.setState({ curTabInd: highlightTemplateTab ? templateIdx : menuInd })
            } else {
                menuInd = userMenu.findIndex((menu) => (paths.startsWith(menu.url)))
                // url in userMenu
                if (menuInd !== -1) this.setState({ curTabInd: -1 })
                // url not in both mainMenu & userMenu
                else this.setState({ curTabInd: -2 })
            }
        } else this.setState({ curTabInd: 0 })
    }

    filterMenuOptions = (menuOptions) => {
        const { store } = this.props
        const { userData, isExploriAdmin } = store
        const userRole = userData.role

        return menuOptions.filter((opt) => opt.roles.includes('all')
                || opt.roles.includes(userRole)
                || (isExploriAdmin() && opt.adminRoles && opt.adminRoles.includes(userRole)))
    }

    mainMenuOptions = () => {
        const { store } = this.props
        const { hasVENU, hasOnlyVENU } = store.client.industryVENU
        // eslint-disable-next-line no-nested-ternary
        const eventSuffix = hasOnlyVENU ? '.only.VENU' : hasVENU ? '.and.VENU' : ''

        return [
            {
                label: i18n.t('navmenu.home'),
                roles: ['all'],
                icon: 'fal fa-house-blank',
                url: '/',
            },
            {
                label: i18n.t('navmenu.intranet'),
                roles: [100],
                icon: 'fal fa-rss',
                url: '/intranet/',
                adminRoles: [110],
                isExternal: true,
                target: '_self',
                style: {
                    'padding-left': '20px',
                },
            },
            {
                label: i18n.t('navmenu.surveys'),
                roles: this.maxbiStatus.nonMaxbi ? [100, 110, 120, 130, 140, 160] : [],
                icon: 'fal fa-clipboard-list',
                url: '/survey/',
                style: {
                    'padding-left': '20px',
                },
            },
            {
                label: i18n.t('navmenu.maxbi'),
                roles: this.maxbiStatus.maxbi ? [100, 110, 120, 130, 140, 160] : [],
                icon: 'fal fa-circle-m',
                url: '/maxbi/',
            },
            {
                label: i18n.t('navmenu.dash'),
                roles: ['all'],
                icon: 'fal fa-gauge-simple-max',
                url: '/company/dashboard',
            },
            {
                label: i18n.t('navmenu.dataExplorer'),
                icon: 'fal fa-table-layout',
                roles: ['all'],
                url: '/dataexplorer/',
            },
            {
                label: i18n.t(`navmenu.eventStructure${eventSuffix}`),
                roles: [100, 110, 120, 130, 140],
                icon: 'fal fa-flag-swallowtail',
                url: '/event/',
                highlightOnUrls: ['/division/', '/portfolio/'],
            },
            {
                label: i18n.t('navmenu.templates'),
                roles: [100, 110],
                icon: 'fal fa-window-restore',
                url: '/survey-templates',
                highlightOnUrls: ['/survey-templates', '/email-templates', '/presentation-templates'],
            },
            {
                label: i18n.t('navmenu.presentations', 'Presentations'),
                roles: store.presentationAccess() ? [100, 110, 120, 130, 140, 150, 170] : [],
                icon: 'fal fa-presentation-screen',
                url: '/presentations',
                highlightOnUrls: ['/presentations'],
            },
            {
                label: i18n.t('settings_isSalesIntelligenceEnabled'),
                roles: store.userData.flags.isSalesIntelligenceEnabled ? [100, 110, 120, 130, 140, 150, 170] : [],
                adminRoles: [],
                icon: 'fa-light fa-magnifying-glass-chart',
                url: '/salesIntelligence',
                highlightOnUrls: ['/salesIntelligence'],
            },
            {
                label: i18n.t('navmenu.questions'),
                roles: [100, 110, 120, 130],
                icon: 'fal fa-message-question',
                url: '/mandatoryquestions/select/',
                highlightOnUrls: ['/mandatoryquestions/', '/question/'],
            },
            {
                label: i18n.t('navmenu.users'),
                roles: [100, 110, 120, 130, 140],
                icon: 'fal fa-user-group',
                url: '/user/',
                style: {
                    'padding-left': '16px',
                },
            },
            {
                label: i18n.t('navmenu.reports'),
                roles: [150, 170],
                icon: 'fal fa-chart-bar',
                url: '/report/',
            },
            {
                label: i18n.t('navmenu.clients'),
                roles: store.isExploriSuper() ? [100] : [],
                icon: 'fal fa-folder-user',
                url: '/client/',
            },
            {
                label: i18n.t('navmenu.venues'),
                roles: [100],
                icon: 'fal fa-circle-location-arrow',
                url: '/venue/',
            },
        ]
    }

    userMenuOptions = () => {
        const { store } = this.props
        const { tokenData } = store
        return ([
            {
                label: i18n.t('navmenu.myInfo'),
                roles: ['all'],
                icon: 'fal fa-file-lines',
                url: `/user/${tokenData.userId}/edit/?userId=${tokenData.userId}`,
                type: 'link',
            },
            {
                label: i18n.t('navmenu.companyInfo'),
                roles: [100, 110],
                icon: 'fal fa-circle-user',
                type: 'modal',
            },
            {
                label: i18n.t('navmenu.changePassword'),
                roles: ['all'],
                icon: 'fal fa-lock',
                url: '/user/updatepassword',
                type: 'link',
            },
        ])
    }

    renderMenuOptions = (menuType) => {
        const {
            curTabInd, menuOptions, isExpand,
        } = this.state
        const { store } = this.props
        const onUserMenuClick = (type) => {
            const { userClientId } = this.state
            if (type === 'modal' && userClientId) store.toggleIsEditClientOpen(true)
            else this.setState({ curTabInd: -1 })
        }

        // return main menu
        if (menuType === 'main') {
            return menuOptions.mainMenu.map((option, index) => (
                <li key={index} className="menu-item">
                    <Anchor
                        href={option.url}
                        onClick={() => { this.setState({ curTabInd: index }) }}
                        cssClass={`${index === curTabInd ? 'active' : ''} ${isExpand ? 'expand' : ''}`}
                        isExternal={option.isExternal}
                        target={option.target}
                        style={{ ...option.style }}
                    >
                        <div className="rect" />
                        <i className={`nav-icon ${option.icon}`} />
                        <span className="nav-name">{option.label}</span>
                        <Tooltip
                            text={option.label}
                            direction="right"
                        />
                    </Anchor>
                </li>
            ))
        }
        // return user menu
        return menuOptions.userMenu.map((option, index) => (
            <li
                key={index}
                className="menu-item"
            >
                <Anchor
                    href={option.type === 'link' ? option.url : null}
                    onClick={() => onUserMenuClick(option.type)}
                >
                    <i className={option.icon} />
                    {option.label}
                </Anchor>
            </li>
        ))
    }

    handleCloseModal = () => {
        const { store } = this.props
        store.toggleIsEditClientOpen(false)
    }

    renderShowDropdown = () => {
        const { store } = this.props
        const { setShowUserDropdown, showUserDropdown } = store
        return this.isSwitchAccountsEnabled
            ? (
                <button
                    className="show-dropdown"
                    type="button"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                />
            ) : null
    }

    changeUser = ({ value }) => {
        this.setState({ selectedUserId: value }, () => {
            this.formRef.current.submit()
        })
    }

    renderUserAccountOption = () => {
        const { selectedUserId } = this.state
        const { store } = this.props
        const currentUserEmail = R.path(['userData', 'email'], store)
        const { tokenData, showUserDropdown, userAccounts } = store

        if (userAccounts?.length <= 1 || !showUserDropdown || !this.isSwitchAccountsEnabled) return null

        const url = process.env.USE_NEW_LOGIN
            ? '/api/login/switchLoggedInUser'
            : '/login/switchLoggedInUser'

        const userAccountOptions = userAccounts.map((ua) => ({
            value: ua.userId,
            label: `${unescapeHtmlTags(ua.clientColumn)} [${ua.role}_USER] - ${ua.name}`,
        }))

        return (
            <form ref={this.formRef} action={url} method="post" className="select-form">
                <input name="email" type="hidden" value={currentUserEmail} />
                <input name="csrf" type="hidden" value={store.csrf} />
                <input name="userid" type="hidden" value={selectedUserId || tokenData.userId} />
                <Select
                    name="account-selector"
                    cssClass="menu-up half-screen"
                    options={userAccountOptions}
                    onBlur={this.changeUser}
                    value={selectedUserId || tokenData.userId}
                    label="Switch account"
                    menuPlacement="top"
                    customSearch={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
                />
            </form>
        )
    }

    toggleAvatarMenu = (e) => {
        if (e.type === 'mouseenter') this.setState({ showAvatarMenu: true })
        if (e.type === 'mousedown'
                && this.avatarMenuRef.current
                && !this.avatarMenuRef.current.contains(e.target)
                && !this.avatarButtonRef.current.contains(e.target)
        ) {
            this.setState({ showAvatarMenu: false })
        }
    }

    render() {
        const {
            curTabInd, overflowActive, userClientId, isExpand, showAvatarMenu,
        } = this.state
        const { store } = this.props
        const { userData } = store
        const firstName = R.pathOr('', ['firstName'], userData)
        const lastName = R.pathOr('', ['lastName'], userData)
        const jobTitle = R.pathOr('', ['jobTitle'], userData)
        const nameInitial = R.pathOr(false, ['0'], firstName)

        return (
            <Fragment>
                {isExpand && <div className="side-overlay" onClick={() => { this.setState({ isExpand: false }) }} />}
                <div
                    id="sidebar"
                    className={`${isExpand ? 'expand' : ''} ${overflowActive ? 'scroll' : ''}`}
                >
                    <div id="up">
                        <button
                            type="button"
                            onClick={() => {
                                this.setState({ isExpand: !isExpand })
                            }}
                        >
                            <div className={`${isExpand ? 'menu-icon open' : 'menu-icon'}`}>
                                <span />
                                <span />
                                <span />
                            </div>
                            {process.env.USE_NEW_HEADER_UI
                                ? null
                                : (
                                    <div id="logo" className={`${isExpand ? 'expand' : ''}`}>
                                        <a href="/">
                                            <img
                                                src={`${process.env.CDN_URL}/images/logo_explori.png`}
                                                alt="Explori Logo"
                                            />
                                        </a>
                                    </div>
                                )}
                        </button>
                    </div>
                    <div id="mid">
                        <ul className="main-menu" ref={this.contentRef}>
                            {this.renderMenuOptions('main')}
                        </ul>
                    </div>
                    <div id="low" className={`${curTabInd === -1 ? 'active' : ''} ${isExpand ? 'expand' : ''}`}>
                        <div className="rect" />
                        <div
                            role="button"
                            tabIndex={0}
                            className="avatar"
                            ref={this.avatarButtonRef}
                            onMouseEnter={this.toggleAvatarMenu}
                        >
                            <div className="initial">
                                {nameInitial || <i className="fa-light fa-user" />}
                            </div>
                            <div className="sidebar-user">
                                <p className="name">{`${firstName} ${lastName}`}</p>
                                {jobTitle.length && <p className="role">{jobTitle}</p>}
                            </div>
                            {showAvatarMenu
                                ? (
                                    <div className="card" ref={this.avatarMenuRef}>
                                        {(firstName && lastName) && (
                                            <>
                                                <div className="card-user">
                                                    <p className="name">{`${firstName} ${lastName}`}</p>
                                                    {jobTitle.length && <p className="role">{jobTitle}</p>}
                                                    {this.renderShowDropdown()}
                                                </div>
                                                <hr className="separator" />
                                            </>
                                        )}
                                        <div className="account-dropdown-wrapper">
                                            {this.renderUserAccountOption()}
                                        </div>
                                        <ul className="user-menu">
                                            {this.renderMenuOptions('user')}
                                        </ul>
                                        <button
                                            type="button"
                                            className="logout"
                                            onClick={() => {
                                                store.mixpanel?.trackLogout()
                                                window.open(process.env.USE_NEW_LOGIN ? '/api/logout' : '/logout/', '_self')
                                            }}
                                        >
                                            <i className="fa-regular fa-arrow-right-from-bracket" />
                                            {i18n.t('/', 'Logout')}
                                        </button>
                                    </div>
                                )
                                : null }
                        </div>
                    </div>
                </div>
                <ClientEdit
                    showModal={store.isEditClientOpen}
                    handleCloseModal={this.handleCloseModal}
                    store={store}
                    clientStore={this.clientStore}
                    clientId={userClientId}
                    role={['100', '110']}
                />
            </Fragment>
        )
    }
}

export default Sidebar
